import * as Phaser from 'phaser';
import { TexasHoldemEngine } from '../engine/TexasHoldemEngine';
import { Card, Player, PlayerAction, BlindConfiguration } from '../types/holdem.types';
import { PokerAIService, AISkillLevel } from '../services/PokerAIService';
import { getPlayerDialogue } from '../data/playerDialogue';
import { UserManager } from '../components/UserManager';
import { SoundControlsPanel } from '../components/SoundControlsPanel';

export class TexasHoldemScene extends Phaser.Scene {
    private engine!: TexasHoldemEngine;
    private blindConfig?: BlindConfiguration;
    private playerAvatars: Map<number, Phaser.GameObjects.Container> = new Map();
    private playerChipTexts: Map<number, Phaser.GameObjects.Text> = new Map();
    private playerBetTexts: Map<number, Phaser.GameObjects.Text> = new Map();
    private playerCardContainers: Map<number, Phaser.GameObjects.Container> = new Map();
    private communityCardContainers: Phaser.GameObjects.Container[] = [];
    private speechBubbles: Map<number, { bubble: Phaser.GameObjects.Graphics, text: Phaser.GameObjects.Text }> = new Map();

    // AI Skill Level Management
    private playerSkillLevels: Map<number, AISkillLevel> = new Map();
    private playerSkillBadges: Map<number, Phaser.GameObjects.Text> = new Map();
    private skillModal: Phaser.GameObjects.Container | null = null;
    private currentSkillSelectionPlayerId: number | null = null;

    // AI Service
    private aiService: PokerAIService;

    // UI Elements
    private potText!: Phaser.GameObjects.Text;
    private currentBetText!: Phaser.GameObjects.Text;
    private phaseText!: Phaser.GameObjects.Text;
    private messageText!: Phaser.GameObjects.Text;

    // Betting controls
    private foldButton!: Phaser.GameObjects.Container;
    private checkButton!: Phaser.GameObjects.Container;
    private callButton!: Phaser.GameObjects.Container;
    private raiseButton!: Phaser.GameObjects.Container;
    private allInButton!: Phaser.GameObjects.Container;
    private raiseSlider!: Phaser.GameObjects.Graphics;
    private raiseAmountText!: Phaser.GameObjects.Text;
    private raiseDecreaseButton!: Phaser.GameObjects.Container;
    private raiseIncreaseButton!: Phaser.GameObjects.Container;
    private raiseAmount: number = 0;

    // Dealer button
    private dealerButton!: Phaser.GameObjects.Container;

    // Back button
    private backButton!: Phaser.GameObjects.Container;

    // Deal button
    private dealButton!: Phaser.GameObjects.Container;

    // Sound controls
    private soundControls?: SoundControlsPanel;

    // Bankroll tracking
    private initialBalance: number = 0;

    constructor() {
        super({ key: 'TexasHoldemScene' });
        this.aiService = new PokerAIService();
    }

    init(data: { blindConfig?: BlindConfiguration }): void {
        // Receive blind configuration from MenuScene
        this.blindConfig = data.blindConfig;
        console.log('[TexasHoldemScene] Received blind configuration:', this.blindConfig);
    }

    preload(): void {
        // Load pre-cropped circular avatar images
        this.load.image('avatar-cowboy', 'avatars/ace_circular.png');
        this.load.image('avatar-woman', 'avatars/luna_circular.png');
        this.load.image('avatar-gambler', 'avatars/maverick_circular.png');
        this.load.image('avatar-oldman', 'avatars/professor_circular.png');
        this.load.image('avatar-joker', 'avatars/wildcard_circular.png');
    }

    create(): void {
        const { width, height } = this.cameras.main;

        // Get player's current balance and name from UserManager if available
        const userManager = UserManager.getInstance();
        const currentUser = userManager.getCurrentUser();
        const playerBalance = currentUser?.balance || 1000;
        const playerName = currentUser?.key || 'You';

        console.log('Texas Hold\'em create() - Loading balance from profile:', playerBalance);

        // Store initial balance for bankroll tracking
        this.initialBalance = playerBalance;

        // Initialize engine with blind configuration
        this.engine = new TexasHoldemEngine(playerBalance, playerName, this.blindConfig);

        // Listen for scene wake event to refresh balance
        this.events.on('wake', this.refreshPlayerBalance, this);

        // Initialize default AI skill levels (all start as 'medium')
        const state = this.engine.getState();
        state.players.forEach(player => {
            if (player.id !== 0) { // Skip human player
                this.playerSkillLevels.set(player.id, 'medium');
            }
        });

        // Create background
        this.createBackground();

        // Create poker table
        this.createPokerTable();

        // Create player positions and avatars
        this.createPlayerPositions();

        // Create community card area
        this.createCommunityCardArea();

        // Create UI
        this.createUI();

        // Create betting controls
        this.createBettingControls();

        // Create back button
        this.createBackButton();

        // Create deal button
        this.createDealButton();

        // Create sound controls
        this.createSoundControls();

        // Show deal button initially instead of auto-starting
        this.dealButton.setVisible(true);
        this.messageText.setText('Ready to play?\nClick DEAL to start!');
    }

    private createBackground(): void {
        const { width, height } = this.cameras.main;

        // Classic casino carpet pattern background
        const bg = this.add.graphics();
        bg.fillStyle(0x1a0033, 1);
        bg.fillRect(0, 0, width, height);

        // Add retro pattern
        for (let x = 0; x < width; x += 20) {
            for (let y = 0; y < height; y += 20) {
                if ((x + y) % 40 === 0) {
                    bg.fillStyle(0x2a0044, 1);
                    bg.fillRect(x, y, 10, 10);
                }
            }
        }
    }

    private createPokerTable(): void {
        const { width, height } = this.cameras.main;
        const centerX = width / 2;
        const centerY = height / 2;

        // Rounded rectangle (stadium shape) poker table - as shown in THLayout.png
        const table = this.add.graphics();
        const tableWidth = 700;
        const tableHeight = 400;
        const cornerRadius = 200;

        // Table shadow
        table.fillStyle(0x000000, 0.5);
        table.fillRoundedRect(centerX - tableWidth/2 + 5, centerY - tableHeight/2 + 5, tableWidth, tableHeight, cornerRadius);

        // Table main surface - classic poker green
        table.fillStyle(0x0a6b0d, 1);
        table.fillRoundedRect(centerX - tableWidth/2, centerY - tableHeight/2, tableWidth, tableHeight, cornerRadius);

        // Table border - darker green
        table.lineStyle(8, 0x085509, 1);
        table.strokeRoundedRect(centerX - tableWidth/2, centerY - tableHeight/2, tableWidth, tableHeight, cornerRadius);

        // Inner border for depth
        table.lineStyle(4, 0x0d8510, 1);
        table.strokeRoundedRect(centerX - tableWidth/2 + 5, centerY - tableHeight/2 + 5, tableWidth - 10, tableHeight - 10, cornerRadius - 5);

        // Felt texture lines for 16-bit style
        table.lineStyle(1, 0x0d8510, 0.3);
        for (let i = -300; i < 300; i += 10) {
            table.moveTo(centerX + i, centerY - 180);
            table.lineTo(centerX + i, centerY + 180);
        }
    }

    private createPlayerPositions(): void {
        const { width, height } = this.cameras.main;
        const centerX = width / 2;
        const centerY = height / 2;

        // 6 player positions - adjusted to prevent screen cutoff
        const positions = [
            { x: centerX - 480, y: centerY + 240, angle: 0 },      // Position 0: Bottom-LEFT (Player)
            { x: centerX - 500, y: centerY + 20, angle: 60 },      // Position 1: Middle-LEFT (Luna)
            { x: centerX - 320, y: centerY - 230, angle: 120 },    // Position 2: Top-left (Ace)
            { x: centerX + 320, y: centerY - 230, angle: 180 },    // Position 3: Top-right (Maverick)
            { x: centerX + 500, y: centerY + 20, angle: 240 },     // Position 4: Middle-RIGHT (Professor)
            { x: centerX + 480, y: centerY + 240, angle: 300 }     // Position 5: Bottom-RIGHT (Wild Card)
        ];

        const state = this.engine.getState();

        state.players.forEach((player, index) => {
            const pos = positions[player.position];

            // Create player container
            const container = this.add.container(pos.x, pos.y);

            // Create avatar (16-bit style)
            this.createPlayerAvatar(container, player);

            // Player name
            const nameText = this.add.text(0, 50, player.name, {
                fontFamily: 'Arial',
                fontSize: player.id === 0 ? '16px' : '14px',
                color: player.id === 0 ? '#FFD700' : '#FFFFFF',
                fontStyle: 'bold',
                stroke: '#000000',
                strokeThickness: 3
            }).setOrigin(0.5);
            container.add(nameText);

            // Player chips
            const chipsText = this.add.text(0, 70, `$${player.chips}`, {
                fontFamily: 'Arial',
                fontSize: '12px',
                color: '#00FF00',
                fontStyle: 'bold',
                stroke: '#000000',
                strokeThickness: 2
            }).setOrigin(0.5);
            container.add(chipsText);
            this.playerChipTexts.set(player.id, chipsText);

            // Add skill level badge for AI players
            if (player.id !== 0) {
                const skillBadge = this.add.text(0, 88, 'Medium', {
                    fontFamily: 'Arial',
                    fontSize: '10px',
                    color: '#00AAFF',
                    fontStyle: 'bold',
                    stroke: '#000000',
                    strokeThickness: 2
                }).setOrigin(0.5);
                container.add(skillBadge);
                this.playerSkillBadges.set(player.id, skillBadge);
            }

            // Card container (will hold 2 cards) - position based on seat location
            // Cards positioned between player avatar and table center
            let cardOffsetX = 0;
            let cardOffsetY = -100;

            switch (player.position) {
                case 0: // Bottom-LEFT (player) - cards toward table (right and up)
                    cardOffsetX = 140;
                    cardOffsetY = -60;
                    break;
                case 1: // Middle-LEFT (Luna) - cards toward table (right)
                    cardOffsetX = 140;
                    cardOffsetY = -10;
                    break;
                case 2: // Top-left (Ace) - cards toward table (right and down)
                    cardOffsetX = 130;
                    cardOffsetY = 80;
                    break;
                case 3: // Top-right (Maverick) - cards toward table (left and down)
                    cardOffsetX = -130;
                    cardOffsetY = 80;
                    break;
                case 4: // Middle-RIGHT (Professor) - cards toward table (left)
                    cardOffsetX = -140;
                    cardOffsetY = -10;
                    break;
                case 5: // Bottom-RIGHT (Wild Card) - cards toward table (left and up)
                    cardOffsetX = -140;
                    cardOffsetY = -60;
                    break;
            }

            const cardContainer = this.add.container(cardOffsetX, cardOffsetY);
            container.add(cardContainer);
            this.playerCardContainers.set(player.id, cardContainer);

            this.playerAvatars.set(player.id, container);
        });
    }

    private createPlayerAvatar(container: Phaser.GameObjects.Container, player: Player): void {
        const size = player.id === 0 ? 50 : 40;

        // Avatar background circle (border)
        const avatarBg = this.add.graphics();
        avatarBg.lineStyle(3, player.id === 0 ? 0xFFD700 : 0x666666, 1);
        avatarBg.strokeCircle(0, 0, size);

        // For player (id 0), use the simple drawn avatar
        if (player.id === 0) {
            const bgCircle = this.add.graphics();
            bgCircle.fillStyle(0x333333, 1);
            bgCircle.fillCircle(0, 0, size);

            const avatar = this.add.graphics();
            this.drawPlayerAvatar(avatar, size);
            container.add([bgCircle, avatar, avatarBg]);
            return;
        }

        // For AI players, use image avatars
        let avatarKey = '';
        switch (player.avatar) {
            case 'cowboy':
                avatarKey = 'avatar-cowboy';
                break;
            case 'woman':
                avatarKey = 'avatar-woman';
                break;
            case 'gambler':
                avatarKey = 'avatar-gambler';
                break;
            case 'oldman':
                avatarKey = 'avatar-oldman';
                break;
            case 'joker':
                avatarKey = 'avatar-joker';
                break;
        }

        if (avatarKey) {
            // Create background circle
            const bgCircle = this.add.graphics();
            bgCircle.fillStyle(0x222222, 1);
            bgCircle.fillCircle(0, 0, size);

            // Check if texture exists before creating image
            if (!this.textures.exists(avatarKey)) {
                container.add([bgCircle, avatarBg]);
                return;
            }

            // Load the pre-cropped circular image - no masking needed!
            const avatarImage = this.add.image(0, 0, avatarKey);
            avatarImage.setDisplaySize(size * 2, size * 2);

            container.add([bgCircle, avatarImage, avatarBg]);

            // Make the avatar image itself interactive - use default hit area based on image bounds
            avatarImage.setInteractive();
            avatarImage.on('pointerdown', () => {
                this.showSkillSelectionModal(player.id);
            });
            avatarImage.on('pointerover', () => {
                avatarBg.clear();
                avatarBg.lineStyle(3, 0x00FFFF, 1);
                avatarBg.strokeCircle(0, 0, size);
            });
            avatarImage.on('pointerout', () => {
                avatarBg.clear();
                avatarBg.lineStyle(3, 0x666666, 1);
                avatarBg.strokeCircle(0, 0, size);
            });
        }
    }

    private drawPlayerAvatar(graphics: Phaser.GameObjects.Graphics, size: number): void {
        // Simple silhouette - player
        const scale = size / 40;

        // Head
        graphics.fillStyle(0x333333, 1);
        graphics.fillCircle(0, -5 * scale, 20 * scale);

        // Shoulders
        graphics.beginPath();
        graphics.arc(0, 15 * scale, 30 * scale, Math.PI, Math.PI * 2);
        graphics.fillPath();
    }

    private drawCowboyAvatar(graphics: Phaser.GameObjects.Graphics, size: number): void {
        // Simple silhouette - person 1
        const scale = size / 40;

        // Head
        graphics.fillStyle(0x444444, 1);
        graphics.fillCircle(0, -5 * scale, 20 * scale);

        // Shoulders
        graphics.beginPath();
        graphics.arc(0, 15 * scale, 30 * scale, Math.PI, Math.PI * 2);
        graphics.fillPath();
    }

    private drawWomanAvatar(graphics: Phaser.GameObjects.Graphics, size: number): void {
        // Simple silhouette - person 2
        const scale = size / 40;

        // Head
        graphics.fillStyle(0x555555, 1);
        graphics.fillCircle(0, -5 * scale, 20 * scale);

        // Shoulders
        graphics.beginPath();
        graphics.arc(0, 15 * scale, 30 * scale, Math.PI, Math.PI * 2);
        graphics.fillPath();
    }

    private drawGamblerAvatar(graphics: Phaser.GameObjects.Graphics, size: number): void {
        // Simple silhouette - person 3
        const scale = size / 40;

        // Head
        graphics.fillStyle(0x666666, 1);
        graphics.fillCircle(0, -5 * scale, 20 * scale);

        // Shoulders
        graphics.beginPath();
        graphics.arc(0, 15 * scale, 30 * scale, Math.PI, Math.PI * 2);
        graphics.fillPath();
    }

    private drawOldManAvatar(graphics: Phaser.GameObjects.Graphics, size: number): void {
        // Simple silhouette - person 4
        const scale = size / 40;

        // Head
        graphics.fillStyle(0x777777, 1);
        graphics.fillCircle(0, -5 * scale, 20 * scale);

        // Shoulders
        graphics.beginPath();
        graphics.arc(0, 15 * scale, 30 * scale, Math.PI, Math.PI * 2);
        graphics.fillPath();
    }

    private drawJokerAvatar(graphics: Phaser.GameObjects.Graphics, size: number): void {
        // Simple silhouette - person 5
        const scale = size / 40;

        // Head
        graphics.fillStyle(0x888888, 1);
        graphics.fillCircle(0, -5 * scale, 20 * scale);

        // Shoulders
        graphics.beginPath();
        graphics.arc(0, 15 * scale, 30 * scale, Math.PI, Math.PI * 2);
        graphics.fillPath();
    }

    private createCommunityCardArea(): void {
        const { width, height } = this.cameras.main;
        const centerX = width / 2;
        const centerY = height / 2;

        // Community cards in center of table as shown in THLayout.png
        // Positioned in upper-center area of table
        for (let i = 0; i < 5; i++) {
            const container = this.add.container(centerX - 170 + (i * 85), centerY - 20);
            container.setVisible(false);
            this.communityCardContainers.push(container);
        }
    }

    private createUI(): void {
        const { width, height } = this.cameras.main;
        const centerX = width / 2;
        const centerY = height / 2;

        // Title at top
        const title = this.add.text(centerX, 20, 'TEXAS HOLD \'EM', {
            fontFamily: 'Arial',
            fontSize: '28px',
            color: '#FFD700',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 4
        }).setOrigin(0.5);

        // Status message - moved up to avoid community cards
        this.messageText = this.add.text(centerX, centerY - 110, 'Hand:\nPlayers Turn', {
            fontFamily: 'Arial',
            fontSize: '16px',
            color: '#FFFFFF',
            fontStyle: 'bold',
            align: 'center',
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5);

        // Pot display in center of table, closer to bottom
        this.add.text(centerX, centerY + 120, 'Pot', {
            fontFamily: 'Arial',
            fontSize: '18px',
            color: '#FFFFFF',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5);

        this.potText = this.add.text(centerX, centerY + 145, '$0', {
            fontFamily: 'Arial',
            fontSize: '20px',
            color: '#FFFFFF',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5);

        // Current bet (hidden - keeping for game logic)
        this.currentBetText = this.add.text(centerX, 60, '', {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#FFD700',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5).setVisible(false);

        // Phase indicator - top right corner
        this.phaseText = this.add.text(width - 100, 50, '', {
            fontFamily: 'Arial',
            fontSize: '16px',
            color: '#FFFFFF',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5);

        // Add "Bet" labels around table
        this.createBetLabels();
    }

    private createBetLabels(): void {
        const { width, height } = this.cameras.main;
        const centerX = width / 2;
        const centerY = height / 2;

        // Dynamic bet amount displays for each player position
        // Positioned on green felt, avoiding card areas
        // Using high-visibility colors (white with black stroke) for readability
        const betLabelStyle = {
            fontFamily: 'Arial',
            fontSize: '16px',
            color: '#FFFFFF',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 3
        };

        const state = this.engine.getState();
        state.players.forEach(player => {
            let betX = centerX;
            let betY = centerY;

            // Position bet text based on player position
            switch (player.position) {
                case 0: // Bottom-LEFT (Player) - bet below cards (adjusted with player position)
                    betX = centerX - 280;
                    betY = centerY + 130;
                    break;
                case 1: // Middle-LEFT (Luna) - bet left of cards
                    betX = centerX - 370;
                    betY = centerY - 60;
                    break;
                case 2: // Top-left (Ace) - bet above cards
                    betX = centerX - 240;
                    betY = centerY - 80;
                    break;
                case 3: // Top-right (Maverick) - bet above cards
                    betX = centerX + 240;
                    betY = centerY - 80;
                    break;
                case 4: // Middle-RIGHT (Professor) - bet right of cards
                    betX = centerX + 370;
                    betY = centerY - 60;
                    break;
                case 5: // Bottom-RIGHT (Wild Card) - bet below cards (adjusted with player position)
                    betX = centerX + 240;
                    betY = centerY + 130;
                    break;
            }

            const betText = this.add.text(betX, betY, '', betLabelStyle).setOrigin(0.5);
            betText.setVisible(false); // Initially hidden
            this.playerBetTexts.set(player.id, betText);
        });
    }

    private createBettingControls(): void {
        const { width, height } = this.cameras.main;

        // Position controls horizontally along bottom of screen, shifted right
        const centerX = width / 2 + 100; // Shift right by 1.5x card width
        const bottomY = height - 75; // Move closer to bottom (about half card height from previous position)
        const buttonGap = 110;

        // Horizontal layout: Fold, Check/Call, Raise, All-in
        // Fold button (far left)
        this.foldButton = this.createButton(centerX - buttonGap * 2.5, bottomY, 'FOLD', 0xFF0000, () => {
            this.handlePlayerAction('fold');
        });

        // Check button
        this.checkButton = this.createButton(centerX - buttonGap * 1.5, bottomY, 'CHECK', 0x00AA00, () => {
            this.handlePlayerAction('check');
        });

        // Call button (same position as check, shown alternatively)
        this.callButton = this.createButton(centerX - buttonGap * 1.5, bottomY, 'CALL', 0x0066FF, () => {
            this.handlePlayerAction('call');
        });

        // Raise button with amount controls
        this.raiseButton = this.createButton(centerX - buttonGap * 0.5, bottomY, 'RAISE', 0xFF8800, () => {
            this.handlePlayerAction('raise', this.raiseAmount);
        });

        // Raise amount display and controls (below raise button)
        this.raiseAmountText = this.add.text(centerX - buttonGap * 0.5, bottomY + 35, '+$20', {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#FFD700',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5);

        // Raise increment buttons (below raise button, aligned with other buttons)
        this.raiseDecreaseButton = this.createSmallButton(centerX - buttonGap * 0.5 - 40, bottomY + 35, '-', () => {
            const state = this.engine.getState();
            this.raiseAmount = Math.max(state.bigBlind, this.raiseAmount - state.bigBlind);
            this.updateRaiseDisplay();
        });

        this.raiseIncreaseButton = this.createSmallButton(centerX - buttonGap * 0.5 + 40, bottomY + 35, '+', () => {
            const state = this.engine.getState();
            const player = this.engine.getPlayer(0);
            if (player) {
                const maxRaise = player.chips - (state.currentBet - player.currentBet);
                this.raiseAmount = Math.min(maxRaise, this.raiseAmount + state.bigBlind);
                this.updateRaiseDisplay();
            }
        });

        // All-in button (center-right)
        this.allInButton = this.createButton(centerX + buttonGap * 0.5, bottomY, 'ALL-IN', 0xFF00FF, () => {
            this.handlePlayerAction('all-in');
        });

        this.raiseAmount = this.engine.getState().bigBlind;
        this.updateButtonVisibility();
    }

    private createButton(x: number, y: number, text: string, color: number, callback: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);

        const bg = this.add.graphics();
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(-40, -20, 80, 40, 8);
        bg.lineStyle(3, 0xFFFFFF, 1);
        bg.strokeRoundedRect(-40, -20, 80, 40, 8);

        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        container.add([bg, buttonText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-40, -20, 80, 40), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);
        container.on('pointerover', () => container.setScale(1.05));
        container.on('pointerout', () => container.setScale(1.0));

        return container;
    }

    private createSmallButton(x: number, y: number, text: string, callback: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);

        const bg = this.add.graphics();
        bg.fillStyle(0x666666, 1);
        bg.fillRoundedRect(-5, -5, 10, 10, 2);
        bg.lineStyle(1, 0xFFFFFF, 1);
        bg.strokeRoundedRect(-5, -5, 10, 10, 2);

        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Arial',
            fontSize: '8px',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        container.add([bg, buttonText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-5, -5, 10, 10), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);
        container.on('pointerover', () => container.setScale(1.2));
        container.on('pointerout', () => container.setScale(1.0));

        return container;
    }

    private createBackButton(): void {
        this.backButton = this.createButton(60, 30, 'MENU', 0x666666, () => {
            // Sync bankroll before leaving
            this.syncBankrollToProfile();

            // Properly shut down this scene before returning to menu
            this.events.off('wake', this.refreshPlayerBalance, this);
            this.scene.stop('TexasHoldemScene');
            this.scene.start('MenuScene');
        });
    }

    private createDealButton(): void {
        const { width, height } = this.cameras.main;
        const centerX = width / 2;
        const centerY = height / 2;

        // Position above "Pot" text, below community cards - moved up more
        this.dealButton = this.createButton(centerX, centerY + 50, 'DEAL', 0x00AA00, () => {
            this.dealButton.setVisible(false);
            this.startNewHand();
        });

        // Initially hidden
        this.dealButton.setVisible(false);
    }

    private createSoundControls(): void {
        if (!this.soundControls) {
            this.soundControls = new SoundControlsPanel(this);
        }
    }

    private startNewHand(): void {
        // Hide deal button when hand starts
        this.dealButton.setVisible(false);

        this.engine.startNewHand();
        this.clearTable();
        this.updateDisplay();
        this.dealCardsWithAnimation();

        // Start AI player turns after a brief delay
        this.time.delayedCall(2000, () => {
            this.processAITurns();
        });
    }

    private clearTable(): void {
        // Clear community cards
        this.communityCardContainers.forEach(container => {
            container.removeAll(true);
            container.setVisible(false);
        });

        // Clear player cards and reset alpha
        this.playerCardContainers.forEach((container, playerId) => {
            container.removeAll(true);
            container.setAlpha(1); // Reset alpha for next hand
        });

        // Clear player bet displays
        this.playerBetTexts.forEach((betText, playerId) => {
            betText.setVisible(false);
        });

        // Clear speech bubbles
        this.clearAllSpeechBubbles();
    }

    private dealCardsWithAnimation(): void {
        const state = this.engine.getState();

        // Deal cards to each player with animation
        state.players.forEach((player, index) => {
            if (player.isActive) {
                this.time.delayedCall(index * 200, () => {
                    this.updatePlayerCards(player.id);
                });
            }
        });
    }

    private updatePlayerCards(playerId: number, forceShowFaceUp: boolean = false): void {
        const player = this.engine.getPlayer(playerId);
        if (!player) return;

        const container = this.playerCardContainers.get(playerId);
        if (!container) return;

        container.removeAll(true);

        // For player (id 0), always show cards face-up
        // For AI players, show face-up at showdown/hand-over or when forced
        const gamePhase = this.engine.getState().phase;
        const shouldShowFaceUp = playerId === 0 || forceShowFaceUp || gamePhase === 'showdown' || gamePhase === 'hand-over';

        player.holeCards.forEach((card, index) => {
            // Cards are 65px wide, centered on their position
            // Card extends from X-32.5 to X+32.5
            // Need 85px spacing minimum to prevent overlap (gives ~20px visible gap)
            const cardX = -42.5 + (index * 85);

            // Always start with card face-down
            const cardGraphic = this.createCard(card, false);
            cardGraphic.setPosition(cardX, 0);

            if (shouldShowFaceUp) {
                // Player cards: flip animation
                cardGraphic.setAlpha(0);
                container.add(cardGraphic);

                this.tweens.add({
                    targets: cardGraphic,
                    alpha: 1,
                    duration: 100,
                    delay: index * 100,
                    onComplete: () => {
                        // Flip animation: scale X to 0, swap content, scale back to 1
                        this.tweens.add({
                            targets: cardGraphic,
                            scaleX: 0,
                            duration: 150,
                            ease: 'Linear',
                            onComplete: () => {
                                // Remove old card and create face-up version
                                const finalCard = this.createCard(card, true);
                                finalCard.setPosition(cardX, 0);
                                finalCard.setScale(0, 1);
                                cardGraphic.destroy();
                                container.add(finalCard);

                                // Scale back to show face-up card
                                this.tweens.add({
                                    targets: finalCard,
                                    scaleX: 1,
                                    duration: 150,
                                    ease: 'Linear'
                                });
                            }
                        });
                    }
                });
            } else {
                // AI cards: slide in animation from center
                const centerX = 0; // Center of card container
                cardGraphic.setPosition(centerX, 0);
                cardGraphic.setAlpha(0);
                container.add(cardGraphic);

                this.tweens.add({
                    targets: cardGraphic,
                    x: cardX,
                    alpha: 1,
                    duration: 300,
                    delay: index * 100,
                    ease: 'Quad.easeOut'
                });
            }
        });
    }

    private createCard(card: Card, faceUp: boolean): Phaser.GameObjects.Container {
        const container = this.add.container(0, 0);
        const cardWidth = 65; // Further increased from 50
        const cardHeight = 90; // Further increased from 70

        // Card background
        const bg = this.add.graphics();
        bg.fillStyle(0xFFFFFF, 1);
        bg.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 3);
        bg.lineStyle(2, 0x000000, 1);
        bg.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 3);

        if (faceUp) {
            // Card rank
            const rankText = this.add.text(-cardWidth / 2 + 5, -cardHeight / 2 + 4, this.getRankDisplay(card.rank), {
                fontFamily: 'Arial',
                fontSize: '22px', // Further increased from 18px
                color: this.getSuitColor(card.suit),
                fontStyle: 'bold'
            }).setOrigin(0, 0);

            // Card suit symbol
            const suitText = this.add.text(0, 0, this.getSuitSymbol(card.suit), {
                fontFamily: 'Arial',
                fontSize: '36px', // Further increased from 28px
                color: this.getSuitColor(card.suit)
            }).setOrigin(0.5, 0.5);

            container.add([bg, rankText, suitText]);
        } else {
            // Card back pattern
            const back = this.add.graphics();
            back.fillStyle(0x0000AA, 1);
            back.fillRoundedRect(-cardWidth / 2 + 2, -cardHeight / 2 + 2, cardWidth - 4, cardHeight - 4, 2);

            // Pattern
            back.lineStyle(1, 0x6666FF, 1);
            for (let i = -10; i < 10; i += 5) {
                back.moveTo(-10 + i, -20);
                back.lineTo(10 + i, 20);
            }
            back.strokePath();

            container.add([bg, back]);
        }

        return container;
    }

    private getRankDisplay(rank: number): string {
        if (rank === 14) return 'A';
        if (rank === 13) return 'K';
        if (rank === 12) return 'Q';
        if (rank === 11) return 'J';
        return rank.toString();
    }

    private getSuitSymbol(suit: string): string {
        switch (suit) {
            case 'hearts': return '♥';
            case 'diamonds': return '♦';
            case 'clubs': return '♣';
            case 'spades': return '♠';
            default: return '';
        }
    }

    private getSuitColor(suit: string): string {
        return (suit === 'hearts' || suit === 'diamonds') ? '#FF0000' : '#000000';
    }

    private updateDisplay(): void {
        const state = this.engine.getState();

        // Update pot
        this.potText.setText(`$${state.pot}`);

        // Update current bet
        if (state.currentBet > 0) {
            this.currentBetText.setText(`Current Bet: $${state.currentBet}`);
        } else {
            this.currentBetText.setText('');
        }

        // Update phase
        this.phaseText.setText(this.getPhaseDisplay(state.phase));

        // Update player chips and bet amounts
        state.players.forEach(player => {
            const chipsText = this.playerChipTexts.get(player.id);
            if (chipsText) {
                chipsText.setText(`$${player.chips}`);

                // Highlight current player
                if (state.currentPlayerIndex === player.position && !player.isFolded) {
                    chipsText.setColor('#FFFF00');
                } else {
                    chipsText.setColor('#00FF00');
                }
            }

            // Update bet amount display
            const betText = this.playerBetTexts.get(player.id);
            if (betText) {
                if (player.currentBet > 0 && !player.isFolded) {
                    betText.setText(`$${player.currentBet}`);
                    betText.setVisible(true);
                } else {
                    betText.setVisible(false);
                }
            }

            // Hide cards for folded players
            if (player.isFolded) {
                const cardContainer = this.playerCardContainers.get(player.id);
                if (cardContainer && cardContainer.list.length > 0 && cardContainer.alpha === 1) {
                    // Fade out the entire container to avoid animation conflicts
                    this.tweens.add({
                        targets: cardContainer,
                        alpha: 0,
                        duration: 300,
                        onComplete: () => {
                            cardContainer.removeAll(true);
                        }
                    });
                }
            }
        });

        // Update dealer button position
        this.updateDealerButton();

        // Update community cards if needed
        this.updateCommunityCards();

        // Update button visibility
        this.updateButtonVisibility();
    }

    private getPhaseDisplay(phase: string): string {
        switch (phase) {
            case 'pre-flop': return 'PRE-FLOP';
            case 'flop': return 'FLOP';
            case 'turn': return 'TURN';
            case 'river': return 'RIVER';
            case 'showdown': return 'SHOWDOWN';
            case 'hand-over': return 'HAND COMPLETE';
            default: return '';
        }
    }

    private updateDealerButton(): void {
        const state = this.engine.getState();
        const dealer = state.players[state.dealerPosition];

        if (!this.dealerButton) {
            this.dealerButton = this.add.container(0, 0);

            const button = this.add.graphics();
            button.fillStyle(0xFFFFFF, 1);
            button.fillCircle(0, 0, 15);
            button.lineStyle(2, 0x000000, 1);
            button.strokeCircle(0, 0, 15);

            const text = this.add.text(0, 0, 'D', {
                fontFamily: 'Arial',
                fontSize: '16px',
                color: '#000000',
                fontStyle: 'bold'
            }).setOrigin(0.5);

            this.dealerButton.add([button, text]);
        }

        const avatar = this.playerAvatars.get(dealer.id);
        if (avatar) {
            this.dealerButton.setPosition(avatar.x + 50, avatar.y - 40);
        }
    }

    private updateCommunityCards(): void {
        const state = this.engine.getState();

        state.communityCards.forEach((card, index) => {
            const container = this.communityCardContainers[index];
            if (container.list.length === 0) {
                // Start with face-down card
                const cardGraphic = this.createCard(card, false);
                container.add(cardGraphic);
                container.setVisible(true);

                // Initial scale animation with staggered delay (400ms between cards)
                container.setScale(0);
                this.tweens.add({
                    targets: container,
                    scaleX: 1,
                    scaleY: 1,
                    duration: 200,
                    delay: index * 400,
                    ease: 'Back.easeOut',
                    onComplete: () => {
                        // After appearing, flip the card over
                        this.tweens.add({
                            targets: cardGraphic,
                            scaleX: 0,
                            duration: 150,
                            delay: 100,
                            ease: 'Linear',
                            onComplete: () => {
                                // Swap to face-up card
                                const faceUpCard = this.createCard(card, true);
                                faceUpCard.setScale(0, 1);
                                cardGraphic.destroy();
                                container.add(faceUpCard);

                                // Scale back to show face-up
                                this.tweens.add({
                                    targets: faceUpCard,
                                    scaleX: 1,
                                    duration: 150,
                                    ease: 'Linear'
                                });
                            }
                        });
                    }
                });
            }
        });
    }

    private updateButtonVisibility(): void {
        const state = this.engine.getState();
        const currentPlayer = this.engine.getCurrentPlayer();
        const player = this.engine.getPlayer(0);
        const isPlayerTurn = currentPlayer.id === 0;
        const callAmount = state.currentBet - currentPlayer.currentBet;

        // Hide all buttons if player is all-in, folded, or hand is over
        if (player && (player.isAllIn || player.isFolded || state.phase === 'hand-over')) {
            this.foldButton.setVisible(false);
            this.checkButton.setVisible(false);
            this.callButton.setVisible(false);
            this.raiseButton.setVisible(false);
            this.allInButton.setVisible(false);
            this.raiseAmountText.setVisible(false);
            this.raiseDecreaseButton.setVisible(false);
            this.raiseIncreaseButton.setVisible(false);
            return;
        }

        // Show/hide buttons based on player's turn and available actions
        this.foldButton.setVisible(isPlayerTurn && state.phase !== 'hand-over');
        this.checkButton.setVisible(isPlayerTurn && callAmount === 0 && state.phase !== 'hand-over');
        this.callButton.setVisible(isPlayerTurn && callAmount > 0 && state.phase !== 'hand-over');
        this.raiseButton.setVisible(isPlayerTurn && currentPlayer.chips > callAmount && state.phase !== 'hand-over');
        this.allInButton.setVisible(isPlayerTurn && state.phase !== 'hand-over');
        this.raiseAmountText.setVisible(isPlayerTurn && state.phase !== 'hand-over');
        this.raiseDecreaseButton.setVisible(isPlayerTurn && state.phase !== 'hand-over');
        this.raiseIncreaseButton.setVisible(isPlayerTurn && state.phase !== 'hand-over');

        // Update call button text
        if (this.callButton.list[1] instanceof Phaser.GameObjects.Text) {
            (this.callButton.list[1] as Phaser.GameObjects.Text).setText(`CALL\n$${callAmount}`);
        }
    }

    private updateRaiseDisplay(): void {
        this.raiseAmountText.setText(`+$${this.raiseAmount}`);
    }

    private handlePlayerAction(action: PlayerAction, raiseAmount?: number): void {
        const success = this.engine.playerAction(action, raiseAmount);

        if (success) {
            this.updateDisplay();

            // Check if we should reveal cards due to all-in situation
            this.checkAndRevealAllInCards();

            this.processAITurns();
        }
    }

    private processAITurns(): void {
        const state = this.engine.getState();
        const currentPlayer = this.engine.getCurrentPlayer();

        console.log(`[processAITurns] ========================================`);
        console.log(`[processAITurns] Current player: ${currentPlayer.id} (${currentPlayer.name})`);
        console.log(`[processAITurns] Phase: ${state.phase}`);
        console.log(`[processAITurns] CurrentPlayerIndex: ${state.currentPlayerIndex}`);
        console.log(`[processAITurns] Player info:`, {
            id: currentPlayer.id,
            name: currentPlayer.name,
            isActive: currentPlayer.isActive,
            isFolded: currentPlayer.isFolded,
            isAllIn: currentPlayer.isAllIn,
            chips: currentPlayer.chips,
            currentBet: currentPlayer.currentBet
        });

        // Check if hand is over
        if (state.phase === 'hand-over') {
            console.log('[processAITurns] Hand is over');
            this.showHandResults();
            return;
        }

        if (state.phase === 'showdown') {
            console.log('[processAITurns] Showdown');
            this.showHandResults();
            return;
        }

        // If it's the player's turn, wait for input
        if (currentPlayer.id === 0) {
            console.log('[processAITurns] *** PLAYER TURN - Waiting for input ***');
            this.messageText.setText('Your turn!');
            this.updateButtonVisibility(); // Make sure buttons are shown
            return;
        }

        // CRITICAL FIX: Check if current player can actually act
        // If they're all-in or folded, they can't act
        if (currentPlayer.isAllIn || currentPlayer.isFolded || !currentPlayer.isActive) {
            console.log(`[processAITurns] Current player ${currentPlayer.name} cannot act (allIn: ${currentPlayer.isAllIn}, folded: ${currentPlayer.isFolded}, active: ${currentPlayer.isActive})`);

            // Check if anyone can act
            const playersWhoCanAct = state.players.filter(p =>
                p.isActive && !p.isFolded && !p.isAllIn
            );

            console.log(`[processAITurns] Players who can still act: ${playersWhoCanAct.length}`);

            if (playersWhoCanAct.length === 0) {
                // No one can act - everyone is all-in or folded
                // Fast-forward through remaining phases to showdown
                console.log('[processAITurns] No one can act - auto-advancing to showdown');
                this.messageText.setText('All players all-in!\nRevealing cards...');

                // Reveal all active players' cards immediately
                state.players.forEach(player => {
                    if (player.isActive && !player.isFolded && player.id !== 0) {
                        this.updatePlayerCards(player.id, true);
                    }
                });

                // Deal remaining community cards and advance to showdown
                this.time.delayedCall(1000, () => {
                    this.advanceAllInToShowdown();
                });
                return;
            }

            // There are players who can act, but current player can't
            // This shouldn't happen (engine should skip them), but handle it gracefully
            console.log('[processAITurns] WARNING: Current player cannot act but others can - this is unexpected');
            console.log('[processAITurns] Calling processAITurns again to find next player');
            this.time.delayedCall(100, () => {
                this.processAITurns();
            });
            return;
        }

        // AI player's turn
        console.log(`[processAITurns] AI player ${currentPlayer.id} turn - calling AI service`);
        this.messageText.setText(`${currentPlayer.name} is thinking...`);

        this.time.delayedCall(1000, async () => {
            // Get player's skill level
            const skillLevel = this.playerSkillLevels.get(currentPlayer.id) || 'medium';

            // Get decision from AI service
            const decision = await this.aiService.getDecision(
                currentPlayer.id,
                currentPlayer.position,
                state.dealerPosition,
                state.smallBlind,
                state.bigBlind,
                state.pot,
                state.currentBet,
                state.phase,
                state.communityCards,
                state.players,
                skillLevel
            );

            // Get personality-driven dialogue instead of AI reasoning
            const dialogue = getPlayerDialogue(currentPlayer.name, decision.action);

            // Show fallback indicator if using fallback logic
            if (decision.usedFallback) {
                this.showFallbackIndicator(currentPlayer.id);
            }

            // Show speech bubble with personality dialogue
            this.showSpeechBubble(currentPlayer.id, dialogue);

            // Execute action
            this.engine.playerAction(decision.action, decision.raise_amount);
            this.updateDisplay();

            // Check if we should reveal cards due to all-in situation
            this.checkAndRevealAllInCards();

            // Continue to next player after a brief delay
            // Speech bubble will stay visible for 4 seconds or until player's next turn
            this.time.delayedCall(1000, () => {
                this.processAITurns();
            });

            // Clear speech bubble after 4 seconds
            this.time.delayedCall(4000, () => {
                this.clearSpeechBubble(currentPlayer.id);
            });
        });
    }

    private showSpeechBubble(playerId: number, text: string): void {
        this.clearSpeechBubble(playerId);

        const avatar = this.playerAvatars.get(playerId);
        if (!avatar) return;

        const player = this.engine.getPlayer(playerId);
        if (!player) return;

        const bubble = this.add.graphics();
        const bubbleWidth = 100;
        const bubbleHeight = 40;
        const bubbleX = avatar.x;

        // Position bubble to side for top players to avoid overlapping with pot/title
        let bubbleY = avatar.y - 120;
        let bubbleOffsetX = 0;

        if (player.position === 3) { // Top center - move bubble to side
            bubbleY = avatar.y;
            bubbleOffsetX = 120;
        } else if (player.position === 2 || player.position === 4) { // Top-left or top-right
            bubbleY = avatar.y - 80; // Less offset for side players
        }

        // Bubble background (apply horizontal offset)
        bubble.fillStyle(0xFFFFFF, 1);
        bubble.fillRoundedRect(bubbleX + bubbleOffsetX - bubbleWidth / 2, bubbleY - bubbleHeight / 2, bubbleWidth, bubbleHeight, 10);
        bubble.lineStyle(2, 0x000000, 1);
        bubble.strokeRoundedRect(bubbleX + bubbleOffsetX - bubbleWidth / 2, bubbleY - bubbleHeight / 2, bubbleWidth, bubbleHeight, 10);

        // Bubble pointer (adjust based on offset)
        bubble.fillStyle(0xFFFFFF, 1);
        if (bubbleOffsetX !== 0) {
            // Horizontal pointer for side bubbles
            bubble.fillTriangle(
                bubbleX + bubbleOffsetX - bubbleWidth / 2, bubbleY - 5,
                bubbleX + bubbleOffsetX - bubbleWidth / 2, bubbleY + 5,
                bubbleX + bubbleOffsetX - bubbleWidth / 2 - 15, bubbleY
            );
            bubble.lineStyle(2, 0x000000, 1);
            bubble.strokeTriangle(
                bubbleX + bubbleOffsetX - bubbleWidth / 2, bubbleY - 5,
                bubbleX + bubbleOffsetX - bubbleWidth / 2, bubbleY + 5,
                bubbleX + bubbleOffsetX - bubbleWidth / 2 - 15, bubbleY
            );
        } else {
            // Vertical pointer for normal bubbles
            bubble.fillTriangle(
                bubbleX - 5, bubbleY + bubbleHeight / 2,
                bubbleX + 5, bubbleY + bubbleHeight / 2,
                bubbleX, bubbleY + bubbleHeight / 2 + 15
            );
            bubble.lineStyle(2, 0x000000, 1);
            bubble.strokeTriangle(
                bubbleX - 5, bubbleY + bubbleHeight / 2,
                bubbleX + 5, bubbleY + bubbleHeight / 2,
                bubbleX, bubbleY + bubbleHeight / 2 + 15
            );
        }

        const bubbleText = this.add.text(bubbleX + bubbleOffsetX, bubbleY, text, {
            fontFamily: 'Arial',
            fontSize: '12px',
            color: '#000000',
            align: 'center',
            wordWrap: { width: bubbleWidth - 10 }
        }).setOrigin(0.5);

        this.speechBubbles.set(playerId, { bubble, text: bubbleText });
    }

    private clearSpeechBubble(playerId: number): void {
        const existing = this.speechBubbles.get(playerId);
        if (existing) {
            existing.bubble.destroy();
            existing.text.destroy();
            this.speechBubbles.delete(playerId);
        }
    }

    private clearAllSpeechBubbles(): void {
        this.speechBubbles.forEach((bubble, playerId) => {
            this.clearSpeechBubble(playerId);
        });
    }

    private showFallbackIndicator(playerId: number): void {
        const avatar = this.playerAvatars.get(playerId);
        if (!avatar) return;

        // Create a small warning indicator (triangle with exclamation mark)
        const indicator = this.add.container(avatar.x + 35, avatar.y - 35);

        // Warning triangle background
        const triangle = this.add.graphics();
        triangle.fillStyle(0xFF6600, 1);
        triangle.fillTriangle(-8, 8, 8, 8, 0, -8);
        triangle.lineStyle(2, 0x000000, 1);
        triangle.strokeTriangle(-8, 8, 8, 8, 0, -8);

        // Exclamation mark
        const warningText = this.add.text(0, -2, '!', {
            fontFamily: 'Arial',
            fontSize: '12px',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        indicator.add([triangle, warningText]);
        indicator.setAlpha(0);

        // Fade in quickly
        this.tweens.add({
            targets: indicator,
            alpha: 1,
            duration: 200,
            ease: 'Quad.easeOut',
            onComplete: () => {
                // Hold for 2 seconds, then fade out
                this.time.delayedCall(2000, () => {
                    this.tweens.add({
                        targets: indicator,
                        alpha: 0,
                        duration: 500,
                        ease: 'Quad.easeIn',
                        onComplete: () => {
                            indicator.destroy();
                        }
                    });
                });
            }
        });
    }

    private checkAndRevealAllInCards(): void {
        const state = this.engine.getState();

        // Find all players who are all-in
        const allInPlayers = state.players.filter(p => p.isAllIn && p.isActive && !p.isFolded);

        // Only proceed if there are all-in players
        if (allInPlayers.length === 0) return;

        const activePlayers = state.players.filter(p => p.isActive && !p.isFolded);

        // Check if there are players who still need to act (haven't matched the bet yet)
        const playersStillToAct = activePlayers.filter(p =>
            !p.isAllIn && p.currentBet < state.currentBet
        );

        // Only reveal cards if NO ONE else needs to act (betting round is complete)
        if (playersStillToAct.length === 0 && activePlayers.length > 1) {
            // Reveal all active players' cards (except human player)
            activePlayers.forEach(player => {
                if (player.id !== 0) {
                    this.updatePlayerCards(player.id, true);
                }
            });
        }
    }

    /**
     * Fast-forward through remaining phases when everyone is all-in
     * This prevents the game from getting stuck in an infinite loop
     */
    private advanceAllInToShowdown(): void {
        console.log('[advanceAllInToShowdown] Starting fast-forward to showdown');
        const state = this.engine.getState();

        // Deal remaining community cards based on current phase
        let cardsToReveal: { phase: string; count: number }[] = [];

        switch (state.phase) {
            case 'pre-flop':
                cardsToReveal = [
                    { phase: 'flop', count: 3 },
                    { phase: 'turn', count: 1 },
                    { phase: 'river', count: 1 }
                ];
                break;
            case 'flop':
                cardsToReveal = [
                    { phase: 'turn', count: 1 },
                    { phase: 'river', count: 1 }
                ];
                break;
            case 'turn':
                cardsToReveal = [
                    { phase: 'river', count: 1 }
                ];
                break;
            case 'river':
                // Already at river, just go to showdown
                cardsToReveal = [];
                break;
        }

        console.log(`[advanceAllInToShowdown] Phase: ${state.phase}, cards to reveal: ${JSON.stringify(cardsToReveal)}`);

        // If there are cards to reveal, show them with animations
        if (cardsToReveal.length > 0) {
            let totalDelay = 0;

            cardsToReveal.forEach((cardSet, setIndex) => {
                this.time.delayedCall(totalDelay, () => {
                    console.log(`[advanceAllInToShowdown] Dealing ${cardSet.count} cards for ${cardSet.phase}`);

                    // Use the engine's internal methods to deal cards
                    // This will update the game state properly
                    if (cardSet.phase === 'flop' && state.communityCards.length === 0) {
                        // Burn card
                        state.deck.pop();
                        // Deal flop (3 cards)
                        for (let i = 0; i < 3; i++) {
                            state.communityCards.push(state.deck.pop()!);
                        }
                        state.phase = 'flop';
                    } else if (cardSet.phase === 'turn' && state.communityCards.length === 3) {
                        // Burn card
                        state.deck.pop();
                        // Deal turn
                        state.communityCards.push(state.deck.pop()!);
                        state.phase = 'turn';
                    } else if (cardSet.phase === 'river' && state.communityCards.length === 4) {
                        // Burn card
                        state.deck.pop();
                        // Deal river
                        state.communityCards.push(state.deck.pop()!);
                        state.phase = 'river';
                    }

                    // Update display to show new community cards
                    this.updateDisplay();
                    this.updateCommunityCards();
                });

                // Each set of cards takes ~2 seconds to animate (400ms per card + delays)
                totalDelay += cardSet.count === 3 ? 2000 : 800;
            });

            // After all cards are revealed, go to showdown
            this.time.delayedCall(totalDelay + 500, () => {
                console.log('[advanceAllInToShowdown] All cards revealed, evaluating showdown');
                // Trigger engine's showdown evaluation
                // We need to access the private method, so use type assertion
                const currentState = this.engine.getState();
                currentState.phase = 'river'; // Set to river first
                (this.engine as any).evaluateShowdown(); // Call private method to evaluate hands
                this.showHandResults();
            });
        } else {
            // No more cards to deal, go straight to showdown
            console.log('[advanceAllInToShowdown] No more cards to deal, going to showdown');
            state.phase = 'river'; // Set to river first
            (this.engine as any).evaluateShowdown(); // Call private method to evaluate hands
            this.showHandResults();
        }
    }

    private showHandResults(): void {
        console.log('[showHandResults] Called');
        const state = this.engine.getState();
        console.log('[showHandResults] Phase:', state.phase);
        console.log('[showHandResults] LastWinners:', state.lastWinners);

        try {
            // Show all remaining players' cards (AI players only, as player's cards are already face-up)
            state.players.forEach(player => {
                if (player.isActive && !player.isFolded && player.id !== 0) {
                    this.updatePlayerCards(player.id, true);
                }
            });

            // Show winner message with hand information
            if (state.lastWinners && state.lastWinners.length > 0) {
                if (state.lastWinners.length === 1) {
                    const winner = state.lastWinners[0];
                    // Use proper grammar: "You win" vs "Name wins"
                    const verb = winner.playerName === 'You' ? 'win' : 'wins';
                    this.messageText.setText(`${winner.playerName} ${verb} $${winner.amount}!\n${winner.handName}`);
                } else {
                    // Split pot
                    const names = state.lastWinners.map(w => w.playerName).join(' & ');
                    const handName = state.lastWinners[0].handName;
                    this.messageText.setText(`Split pot: ${names}\n${handName}`);
                }
            } else {
                // Fallback for when winner info isn't available
                const activePlayers = state.players.filter(p => p.isActive && !p.isFolded);
                if (activePlayers.length === 1) {
                    const winner = activePlayers[0];
                    const verb = winner.name === 'You' ? 'win' : 'wins';
                    this.messageText.setText(`${winner.name} ${verb}!`);
                } else {
                    this.messageText.setText('Hand complete');
                }
            }

            // Sync player's bankroll to profile after hand completes
            this.syncBankrollToProfile();
        } catch (error) {
            console.error('[showHandResults] Error:', error);
            this.messageText.setText('Hand complete\nClick DEAL for next hand');
        }

        // ALWAYS show deal button, even if there was an error
        console.log('[showHandResults] Showing deal button');
        this.dealButton.setVisible(true);
        this.dealButton.setDepth(1000); // Ensure it's visible on top
    }

    private syncBankrollToProfile(): void {
        const userManager = UserManager.getInstance();
        const player = this.engine.getPlayer(0);

        if (!player) {
            console.warn('Texas Hold\'em: Player not found for sync');
            return;
        }

        // Calculate net change from initial balance
        const currentBalance = player.chips;
        const netChange = currentBalance - this.initialBalance;
        const won = netChange > 0 ? netChange : 0;
        const lost = netChange < 0 ? Math.abs(netChange) : 0;

        console.log(`Texas Hold'em: Saving balance ${currentBalance}, won: ${won}, lost: ${lost}`);

        // Call updateBalance without await - fire and forget
        // The UserManager will handle the async API call internally
        userManager.updateBalance(currentBalance, won, lost).catch((err: Error) => {
            console.error('Failed to sync bankroll:', err);
        });

        // Update initial balance for next hand
        this.initialBalance = currentBalance;
    }

    private refreshPlayerBalance(): void {
        // Get current profile balance
        const userManager = UserManager.getInstance();
        const currentUser = userManager.getCurrentUser();

        if (!currentUser) return;

        const player = this.engine.getPlayer(0);
        if (!player) return;

        // Update player's chips to match profile balance
        const profileBalance = currentUser.balance;
        player.chips = profileBalance;
        this.initialBalance = profileBalance;

        // Update chip display
        const chipsText = this.playerChipTexts.get(0);
        if (chipsText) {
            chipsText.setText(`$${player.chips}`);
        }

        console.log(`Texas Hold'em balance refreshed: $${profileBalance}`);
    }

    // =============================================================================
    // AI Skill Level Selection
    // =============================================================================

    private showSkillSelectionModal(playerId: number): void {
        // Close existing modal if open
        if (this.skillModal) {
            this.closeSkillSelectionModal();
        }

        this.currentSkillSelectionPlayerId = playerId;
        const player = this.engine.getPlayer(playerId);
        if (!player) return;

        const { width, height } = this.cameras.main;
        const centerX = width / 2;
        const centerY = height / 2;

        // Create modal container
        this.skillModal = this.add.container(0, 0);
        this.skillModal.setDepth(1000); // Ensure it's on top

        // Overlay background (semi-transparent)
        const overlay = this.add.graphics();
        overlay.fillStyle(0x000000, 0.7);
        overlay.fillRect(0, 0, width, height);
        overlay.setInteractive(new Phaser.Geom.Rectangle(0, 0, width, height), Phaser.Geom.Rectangle.Contains);
        overlay.on('pointerdown', () => {
            this.closeSkillSelectionModal();
        });
        this.skillModal.add(overlay);

        // Modal panel - sized to fit 6 buttons snugly
        const modalWidth = 400;
        const modalHeight = 410;
        const panel = this.add.graphics();
        panel.fillStyle(0x1a0033, 1);
        panel.fillRoundedRect(centerX - modalWidth / 2, centerY - modalHeight / 2, modalWidth, modalHeight, 10);
        panel.lineStyle(4, 0xFFD700, 1);
        panel.strokeRoundedRect(centerX - modalWidth / 2, centerY - modalHeight / 2, modalWidth, modalHeight, 10);
        this.skillModal.add(panel);

        // Title
        const title = this.add.text(centerX, centerY - modalHeight / 2 + 30, `${player.name}'s Skill Level`, {
            fontFamily: 'Arial',
            fontSize: '24px',
            color: '#FFD700',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 4
        }).setOrigin(0.5);
        this.skillModal.add(title);

        // Skill level buttons - comfortably spaced
        const buttonSpacing = 60;
        const startY = centerY - modalHeight / 2 + 70;

        const skills: { level: AISkillLevel; label: string; description: string; color: number }[] = [
            { level: 'easy', label: 'Easy', description: 'Beginner play', color: 0x00AA00 },
            { level: 'medium', label: 'Medium', description: 'Balanced strategy', color: 0x0066FF },
            { level: 'hard', label: 'Hard', description: 'Advanced tactics', color: 0xFF8800 },
            { level: 'expert', label: 'Expert', description: 'Near-optimal play', color: 0xFF0000 },
            { level: 'random', label: 'Random', description: 'Picks one level randomly', color: 0xAA00AA },
            { level: 'pure-chaos', label: 'Pure Chaos', description: 'Changes every action!', color: 0xFF00FF }
        ];

        skills.forEach((skill, index) => {
            const buttonY = startY + (index * buttonSpacing);
            const button = this.createSkillButton(centerX, buttonY, skill.label, skill.description, skill.color, () => {
                this.setPlayerSkillLevel(playerId, skill.level);
                this.closeSkillSelectionModal();
            });
            this.skillModal!.add(button);
        });
    }

    private createSkillButton(
        x: number,
        y: number,
        label: string,
        description: string,
        color: number,
        callback: () => void
    ): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);

        // Button background
        const bg = this.add.graphics();
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(-180, -22, 360, 44, 8);
        bg.lineStyle(3, 0xFFFFFF, 1);
        bg.strokeRoundedRect(-180, -22, 360, 44, 8);

        // Label text
        const labelText = this.add.text(-170, -10, label, {
            fontFamily: 'Arial',
            fontSize: '18px',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0, 0.5);

        // Description text
        const descText = this.add.text(170, -10, description, {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#CCCCCC',
            fontStyle: 'italic'
        }).setOrigin(1, 0.5);

        container.add([bg, labelText, descText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-180, -22, 360, 44), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);
        container.on('pointerover', () => {
            container.setScale(1.05);
            bg.clear();
            bg.fillStyle(color, 1);
            bg.fillRoundedRect(-180, -22, 360, 44, 8);
            bg.lineStyle(3, 0x00FFFF, 1);
            bg.strokeRoundedRect(-180, -22, 360, 44, 8);
        });
        container.on('pointerout', () => {
            container.setScale(1.0);
            bg.clear();
            bg.fillStyle(color, 1);
            bg.fillRoundedRect(-180, -22, 360, 44, 8);
            bg.lineStyle(3, 0xFFFFFF, 1);
            bg.strokeRoundedRect(-180, -22, 360, 44, 8);
        });

        return container;
    }

    private closeSkillSelectionModal(): void {
        if (this.skillModal) {
            this.skillModal.destroy();
            this.skillModal = null;
        }
        this.currentSkillSelectionPlayerId = null;
    }

    private setPlayerSkillLevel(playerId: number, skillLevel: AISkillLevel): void {
        this.playerSkillLevels.set(playerId, skillLevel);

        // Clear random skill cache when changing skill level
        this.aiService.clearRandomSkillCache(playerId);

        // Update badge display
        const badge = this.playerSkillBadges.get(playerId);
        if (badge) {
            badge.setText(this.getSkillLevelDisplay(skillLevel));
            badge.setColor(this.getSkillLevelColor(skillLevel));
        }
    }

    private getSkillLevelDisplay(skillLevel: AISkillLevel): string {
        switch (skillLevel) {
            case 'easy': return 'Easy';
            case 'medium': return 'Medium';
            case 'hard': return 'Hard';
            case 'expert': return 'Expert';
            case 'random': return 'Random';
            case 'pure-chaos': return 'Chaos';
        }
    }

    private getSkillLevelColor(skillLevel: AISkillLevel): string {
        switch (skillLevel) {
            case 'easy': return '#00FF00';
            case 'medium': return '#00AAFF';
            case 'hard': return '#FF8800';
            case 'expert': return '#FF0000';
            case 'random': return '#AA00FF';
            case 'pure-chaos': return '#FF00FF';
        }
    }

    update(): void {
        // Update logic if needed
    }
}
